import { FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Field,
  Heading,
  Input,
  Label,
  Paragraph,
  ValidationMessage,
} from '@digdir/designsystemet-react';
import { ArrowLeftIcon, EyeSlashIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import { Routes } from '../../../../enums/Routes';
import { useCreateTeamMutation } from '../../../../hooks/mutations';
import { useSelectedUnitContext } from '../../../../contexts/selectedUnitContext';
import { RouterUtilsImpl } from '../../../../utils/RouterUtils';
import { SurveyFormCardUtils } from '../../../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { OnboardingBanner } from '../../../GettingStarted/OnboardingBanner';
import { guidanceFrom, homeFor, surveyStepPath } from '../../../GettingStarted/gettingStartedView';
import classes from './CreateTeamPage.module.css';

const MINIMUM_MEMBERS = 3;
const routeUtils = new RouterUtilsImpl();

type FieldErrors = { name?: string; members?: string };
type CreatedTeam = { id: number; name: string };

export const CreateTeamPage = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedTeamId } = useSelectedUnitContext();
  const createTeamMutation = useCreateTeamMutation();
  // The first step of «Kom i gang»: the same screen, with a banner and the survey next
  const [params] = useSearchParams();
  const guidance = guidanceFrom(params);

  const isOrganizationPath = location.pathname.includes('/organization/');
  const backRoute = guidance
    ? (homeFor(guidance.goal) ?? Routes.Teams)
    : isOrganizationPath
      ? Routes.OrganizationTeamsOverview
      : Routes.Teams;
  const context = isOrganizationPath ? 'organization' : 'personal';

  const [name, setName] = useState('');
  const [members, setMembers] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [errorTick, setErrorTick] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdTeam, setCreatedTeam] = useState<CreatedTeam | null>(null);
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const successRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (errorTick > 0) errorSummaryRef.current?.focus();
  }, [errorTick]);

  useEffect(() => {
    if (createdTeam) successRef.current?.focus();
  }, [createdTeam]);

  const validate = (): FieldErrors => {
    const result: FieldErrors = {};
    if (!name.trim()) result.name = t('teams.create.validation.nameRequired');
    // The API wants at least two characters
    else if (name.trim().length < 2) result.name = t('teams.create.validation.nameTooShort');
    const count = Number(members);
    if (!/^\d+$/.test(members.trim()) || count < MINIMUM_MEMBERS) {
      result.members = t('teams.create.validation.membersMinimum');
    }
    return result;
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);
    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      setErrorTick((tick) => tick + 1);
      return;
    }

    const team = { name: name.trim(), numberOfTeamMembers: Number(members) };
    createTeamMutation.mutate(team, {
      onSuccess: (response) => {
        setCreatedTeam({ id: response.data.id, name: response.data.name ?? team.name });
      },
      onError: (error: any) => {
        setSubmitError(error?.response?.data?.message || t('teams.create.notifications.createError'));
      },
    });
  };

  const goToSurveyCreation = () => {
    if (!createdTeam) return;
    SurveyFormCardUtils.removeAllSessionKeys();
    setSelectedTeamId(createdTeam.id);
    navigate(guidance ? surveyStepPath(guidance.goal, createdTeam.id) : Routes.CreateSurvey);
  };

  const errorEntries = [
    errors.name && { href: '#team-name', message: errors.name },
    errors.members && { href: '#team-members', message: errors.members },
  ].filter(Boolean) as Array<{ href: string; message: string }>;

  return (
    <div className={classes.page}>
      <div aria-hidden="true" className={classes.backdrop} />
      <div className={classes.content}>
        <Link to={backRoute} className={classes.backLink}>
          <ArrowLeftIcon aria-hidden="true" fontSize="1.25rem" />
          {guidance ? t('gettingStarted.banner.back') : t(`teams.create.backLink.${context}`)}
        </Link>
        {guidance && (
          <OnboardingBanner
            withBackLink={false}
            goal={guidance.goal}
            step={createdTeam ? 2 : 1}
            tip={createdTeam ? t('gettingStarted.banner.tips.teamCreated') : t('gettingStarted.banner.tips.createTeam')}
          />
        )}

        {createdTeam ? (
          <div className={classes.card}>
            <div className={classes.successBody}>
              <Alert data-color="success" role="status" ref={successRef} tabIndex={-1}>
                <Heading level={1} data-size="2xs" className={classes.alertHeading}>
                  {t('teams.create.success.title', { teamName: createdTeam.name })}
                </Heading>
                <Paragraph>{t('teams.create.success.description')}</Paragraph>
              </Alert>
              {/* In «Kom i gang» the survey is the next step, and the channels can wait */}
              <div className={classes.successActions}>
                {guidance && <Button onClick={goToSurveyCreation}>{t('teams.create.success.createSurvey')}</Button>}
                <Button asChild variant={guidance ? 'secondary' : 'primary'}>
                  <Link to={routeUtils.getRoute('TeamIntegration', String(createdTeam.id))}>
                    {t('teams.create.success.chooseChannels')}
                  </Link>
                </Button>
                {!guidance && (
                  <Button variant="secondary" onClick={goToSurveyCreation}>
                    {t('teams.create.success.createSurvey')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <header className={classes.header}>
              <span aria-hidden="true" className={classes.icon}>
                <PersonGroupIcon />
              </span>
              <div className={classes.heading}>
                <span className={classes.eyebrow}>{t(`teams.create.eyebrow.${context}`)}</span>
                <Heading level={1} data-size="xl" className={classes.title}>
                  {t('teams.create.pageTitle')}
                </Heading>
              </div>
            </header>

            <form className={classes.card} onSubmit={onSubmit} noValidate>
              <div className={classes.body}>
                {errorEntries.length > 0 && (
                  <div className="ds-error-summary" ref={errorSummaryRef} tabIndex={-1}>
                    <Heading level={2} data-size="2xs">
                      {t('teams.create.validation.summaryTitle')}
                    </Heading>
                    <ul className="ds-list">
                      {errorEntries.map((entry) => (
                        <li key={entry.href}>
                          <a href={entry.href} className="ds-link">
                            {entry.message}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Field>
                  <Label htmlFor="team-name">{t('teams.create.form.nameLabel')}</Label>
                  <Paragraph data-size="sm" id="team-name-description" className={classes.subtle}>
                    {t('teams.create.form.nameDescription')}
                  </Paragraph>
                  <Input
                    id="team-name"
                    name="name"
                    autoComplete="off"
                    placeholder={t('teams.create.form.namePlaceholder')}
                    aria-describedby="team-name-description"
                    aria-invalid={errors.name ? true : undefined}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {errors.name && <ValidationMessage>{errors.name}</ValidationMessage>}
                </Field>

                <Field>
                  <Label htmlFor="team-members">{t('teams.create.form.membersLabel')}</Label>
                  <Paragraph data-size="sm" id="team-members-description" className={classes.subtle}>
                    {t('teams.create.form.membersDescription')}
                  </Paragraph>
                  <Input
                    id="team-members"
                    name="numberOfTeamMembers"
                    type="number"
                    inputMode="numeric"
                    min={MINIMUM_MEMBERS}
                    className={classes.membersInput}
                    aria-describedby="team-members-description"
                    aria-invalid={errors.members ? true : undefined}
                    value={members}
                    onChange={(e) => setMembers(e.target.value)}
                  />
                  {errors.members && <ValidationMessage>{errors.members}</ValidationMessage>}
                </Field>

                <div className={classes.info}>
                  <EyeSlashIcon aria-hidden="true" className={classes.infoIcon} />
                  <Paragraph data-size="sm">{t('teams.create.form.anonymityInfo')}</Paragraph>
                </div>

                {submitError && (
                  <Alert data-color="danger">
                    <Heading level={2} data-size="2xs" className={classes.alertHeading}>
                      {t('teams.create.error.title')}
                    </Heading>
                    <Paragraph>{submitError}</Paragraph>
                  </Alert>
                )}
              </div>

              <div className={classes.footer}>
                <Button asChild variant="tertiary">
                  <Link to={backRoute}>{t('teams.create.buttons.cancel')}</Link>
                </Button>
                <Button type="submit" disabled={createTeamMutation.isPending}>
                  {createTeamMutation.isPending
                    ? t('teams.create.buttons.creating')
                    : t('teams.create.buttons.create')}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
